import { isAxiosError } from 'axios'

import { isErrorType } from '@/config/http/http.helpers'
import {
  StoreFundingAttempt,
  StoreFundingMethodsResponse,
  StoreFundingPaymentMethod
} from '@/ducks/billing/billing.wallet-funding.types'

export const parseWalletMoney = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return undefined
}

export const formatWalletMoneyAmount = (value: number): string => {
  return value.toFixed(2)
}

export const listWithdrawPaymentMethods = (
  response?: StoreFundingMethodsResponse | null
): StoreFundingPaymentMethod[] => {
  return (response?.paymentMethods ?? []).filter(
    method =>
      method.capabilities?.withdraw?.enabled === true && method.recipientReady
  )
}

export const getFundingPaymentMethodLabel = (
  method?: StoreFundingPaymentMethod | null
): string => {
  if (!method) return ''

  return (
    method.presentation?.summary?.title?.trim() ||
    method.displayName?.trim() ||
    method.paymentMethodId
  )
}

export const selectWithdrawPaymentMethod = (
  response?: StoreFundingMethodsResponse | null
): StoreFundingPaymentMethod | undefined => {
  return listWithdrawPaymentMethods(response)[0]
}

/** Store App LD project — Boolean, country-targeted (e.g. TN → true). */
export const WALLET_PAYOUT_LD_FLAG_KEY = 'LMD-Store-Web-WalletPayout-Enabled'

export const canRequestStoreWalletPayout = (params: {
  walletSource?: string
  balance: number
  isWalletFeatureEnabled: boolean
  isWalletPayoutEnabled: boolean
  hasWithdrawPaymentMethod: boolean
  isStoreOwner: boolean
}): boolean => {
  if (!params.isWalletFeatureEnabled) return false
  if (!params.isWalletPayoutEnabled) return false
  if (!params.isStoreOwner) return false
  if (!params.hasWithdrawPaymentMethod) return false
  return params.walletSource === 'store-wallet' && params.balance > 0
}

export const validateWithdrawAmount = (params: {
  amount: string
  balance: number
  method?: StoreFundingPaymentMethod
}): string | null => {
  const parsedAmount = parseWalletMoney(params.amount)
  if (parsedAmount == null || parsedAmount <= 0) {
    return 'billing.wallet.payout.errors.invalid-amount'
  }

  const min =
    parseWalletMoney(params.method?.capabilities?.withdraw?.limits?.min) ?? 0
  const max = parseWalletMoney(params.method?.capabilities?.withdraw?.limits?.max)

  if (parsedAmount < min) {
    return 'billing.wallet.payout.errors.below-min'
  }
  if (typeof max === 'number' && parsedAmount > max) {
    return 'billing.wallet.payout.errors.above-max'
  }
  if (parsedAmount > params.balance) {
    return 'billing.wallet.payout.errors.insufficient-balance'
  }

  return null
}

export const createWithdrawIdempotencyKey = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `withdraw-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export const copyWalletTextToClipboard = async (text: string): Promise<boolean> => {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
    return false
  }

  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

export const getFundingUnavailableMessageKey = (params: {
  fundingMethods?: StoreFundingMethodsResponse | null
  fundingMethodsError?: unknown
  isFundingMethodsLoading?: boolean
}): string | null => {
  if (params.isFundingMethodsLoading) return null

  if (params.fundingMethodsError) {
    return 'billing.wallet.payout.unavailable-request-failed'
  }

  const methods = params.fundingMethods?.paymentMethods ?? []
  if (!methods.length) {
    return 'billing.wallet.payout.unavailable-country'
  }

  const withdrawMethod = methods.find(
    method => method.capabilities?.withdraw?.enabled === true
  )
  if (withdrawMethod && !withdrawMethod.recipientReady) {
    return 'billing.wallet.payout.unavailable-profile'
  }

  if (!selectWithdrawPaymentMethod(params.fundingMethods)) {
    return 'billing.wallet.payout.unavailable'
  }

  return null
}

export const isTerminalFundingState = (state?: string): boolean => {
  const normalized = String(state || '').toUpperCase()
  return (
    normalized === 'SUCCEEDED' ||
    normalized === 'FAILED' ||
    normalized === 'CANCELLED'
  )
}

export const isFailedFundingAttempt = (
  attempt?: Pick<StoreFundingAttempt, 'state'> | null
): boolean => {
  const normalized = String(attempt?.state || '').toUpperCase()
  return normalized === 'FAILED' || normalized === 'CANCELLED'
}

export const getWithdrawFailureMessageKey = (
  attempt?: Pick<StoreFundingAttempt, 'state' | 'terminalReasonCode'> | null
): string | null => {
  if (!attempt || !isFailedFundingAttempt(attempt)) return null

  const reasonCode = String(attempt.terminalReasonCode || '').toUpperCase()
  if (reasonCode.startsWith('EPAY_')) {
    return 'billing.wallet.payout.errors.withdraw-payment-unavailable'
  }

  return 'billing.wallet.payout.errors.withdraw-failed'
}

const AMBIGUOUS_WITHDRAW_HTTP_STATUSES = new Set([408, 502, 503, 504])

export const isWithdrawRequestAmbiguousError = (error: unknown): boolean => {
  if (isAxiosError(error)) {
    if (
      error.code === 'ECONNABORTED' ||
      error.code === 'ERR_NETWORK' ||
      !error.response
    ) {
      return true
    }

    const status = error.response.status
    return AMBIGUOUS_WITHDRAW_HTTP_STATUSES.has(status)
  }

  if (!isErrorType(error)) return false

  if (
    error.code === 'ECONNABORTED' ||
    error.code === 'ERR_NETWORK' ||
    !error.status
  ) {
    return true
  }

  const status = Number(error.status)
  return AMBIGUOUS_WITHDRAW_HTTP_STATUSES.has(status)
}

export const isWithdrawRecoveredSuccess = (
  attempt?: Pick<StoreFundingAttempt, 'state' | 'nextAction'> | null
): boolean => {
  const state = String(attempt?.state || '').toUpperCase()
  if (state === 'SUCCEEDED') return true

  if (state === 'PROCESSING' || state === 'REQUESTED') {
    return attempt?.nextAction?.type === 'SHOW_AGENT_INSTRUCTIONS'
  }

  return false
}

export const getWithdrawTimeoutMessageKey = (
  attempt?: Pick<StoreFundingAttempt, 'state' | 'nextAction'> | null
): string => {
  if (isWithdrawRecoveredSuccess(attempt)) {
    return 'billing.wallet.payout.errors.timeout-succeeded'
  }

  return 'billing.wallet.payout.errors.timeout-failed'
}

export const STORE_AGENT_CODE_VALIDITY_MS = 24 * 60 * 60 * 1000

export const isAgentWithdrawalCodeExpired = (params: {
  expiresAt?: string
  createdAt?: string
}): boolean => {
  const expiresAt = params.expiresAt?.trim()
  if (expiresAt) {
    const expiryTime = Date.parse(expiresAt)
    return Number.isFinite(expiryTime) && Date.now() > expiryTime
  }

  const createdAt = params.createdAt?.trim()
  if (!createdAt) return false

  const createdTime = Date.parse(createdAt)
  if (!Number.isFinite(createdTime)) return false

  return Date.now() > createdTime + STORE_AGENT_CODE_VALIDITY_MS
}

export const resolveWalletAgentInstructions = (
  fundingAttempt?: Pick<
    StoreFundingAttempt,
    'nextAction' | 'clientHints' | 'createdAt'
  > | null
) => {
  const nextActionInstructions =
    fundingAttempt?.nextAction?.type === 'SHOW_AGENT_INSTRUCTIONS'
      ? fundingAttempt.nextAction.instructions
      : undefined

  if (nextActionInstructions?.code) {
    return nextActionInstructions
  }

  const providerOrderId = String(
    fundingAttempt?.clientHints?.providerOrderId || ''
  ).trim()

  if (!providerOrderId) return null

  const createdAt = fundingAttempt?.createdAt
  const createdTime = createdAt ? Date.parse(createdAt) : NaN
  const expiresAt = Number.isFinite(createdTime)
    ? new Date(createdTime + STORE_AGENT_CODE_VALIDITY_MS).toISOString()
    : undefined

  return {
    title: undefined,
    body: undefined,
    code: providerOrderId,
    expiresAt
  }
}

export const isCashOutWalletTransactionType = (transactionType?: string) => {
  const normalizedType = String(transactionType || '').toUpperCase()
  return (
    normalizedType.includes('CASH_OUT') ||
    (normalizedType.includes('CASH') && normalizedType.includes('OUT'))
  )
}

export const resolveEmbeddedWalletAgentInstructions = (transaction?: {
  transactionType?: string
  nextAction?: StoreFundingAttempt['nextAction']
} | null) => {
  if (!transaction || !isCashOutWalletTransactionType(transaction.transactionType)) {
    return null
  }

  if (transaction.nextAction?.type !== 'SHOW_AGENT_INSTRUCTIONS') {
    return null
  }

  return transaction.nextAction.instructions || null
}

export const shouldShowWalletAgentCode = (params: {
  fundingAttempt?: Pick<
    StoreFundingAttempt,
    'state' | 'nextAction' | 'clientHints' | 'createdAt'
  > | null
  transactionStatus?: string
}): boolean => {
  if (isFailedFundingAttemptState(params.fundingAttempt)) return false

  const transactionStatus = String(params.transactionStatus || '').toUpperCase()
  const fundingState = String(params.fundingAttempt?.state || '').toUpperCase()

  // Paid / settled / terminal cash-out — code is no longer actionable.
  if (
    fundingState === 'SUCCEEDED' ||
    isNonActionableWalletTransactionStatus(transactionStatus)
  ) {
    return false
  }

  const instructions = resolveWalletAgentInstructions(params.fundingAttempt)
  if (!instructions?.code) return false

  const hasLiveAgentInstructions =
    params.fundingAttempt?.nextAction?.type === 'SHOW_AGENT_INSTRUCTIONS'

  if (transactionStatus === 'PENDING' && hasLiveAgentInstructions) {
    return true
  }

  return !isAgentWithdrawalCodeExpired({
    expiresAt: instructions.expiresAt,
    createdAt: params.fundingAttempt?.createdAt
  })
}

export const shouldShowEmbeddedWalletAgentCode = (params: {
  transaction?: {
    status?: string
    transactionType?: string
    nextAction?: StoreFundingAttempt['nextAction']
  } | null
}): boolean => {
  const instructions = resolveEmbeddedWalletAgentInstructions(params.transaction)
  if (!instructions?.code) return false

  const transactionStatus = String(params.transaction?.status || '').toUpperCase()
  if (transactionStatus === 'PENDING') return true

  return !isAgentWithdrawalCodeExpired({
    expiresAt: instructions.expiresAt
  })
}

export const isFailedFundingAttemptState = (
  fundingAttempt?: Pick<StoreFundingAttempt, 'state'> | null
): boolean => {
  const state = String(fundingAttempt?.state || '').toUpperCase()
  return state === 'FAILED' || state === 'CANCELLED'
}

export const isSettledWalletCashOutStatus = (transactionStatus?: string): boolean => {
  const normalizedStatus = String(transactionStatus || '').toUpperCase()
  return normalizedStatus === 'CONFIRMED' || normalizedStatus === 'DONE'
}

export const resolveWalletListTransactionStatus = (
  status: 'pending' | 'done' | 'refund' | 'failed' | 'canceled'
): string => {
  if (status === 'done') return 'CONFIRMED'
  if (status === 'failed') return 'FAILED'
  if (status === 'canceled') return 'CANCELLED'
  if (status === 'refund') return 'REFUNDED'
  return 'PENDING'
}

const isNonActionableWalletTransactionStatus = (
  transactionStatus?: string
): boolean => {
  const normalizedStatus = String(transactionStatus || '').toUpperCase()
  return (
    normalizedStatus === 'CONFIRMED' ||
    normalizedStatus === 'DONE' ||
    normalizedStatus === 'FAILED' ||
    normalizedStatus === 'REFUND' ||
    normalizedStatus === 'REFUNDED' ||
    normalizedStatus === 'CANCELLED'
  )
}

export const isSucceededFundingAttemptState = (
  fundingAttempt?: Pick<StoreFundingAttempt, 'state'> | null
): boolean => {
  return String(fundingAttempt?.state || '').toUpperCase() === 'SUCCEEDED'
}

export const getWalletSettlementSourceLabelKey = (
  source?: string | null
): string => {
  const normalizedSource = String(source || '').toLowerCase()
  if (normalizedSource === 'webhook' || normalizedSource === 'sync') {
    return `billing.wallet.details.settlementSource.${normalizedSource}`
  }
  return 'billing.wallet.details.settlementSource.unknown'
}

export const shouldShowExpiredWalletAgentCodeHint = (params: {
  fundingAttempt?: Pick<
    StoreFundingAttempt,
    'state' | 'nextAction' | 'clientHints' | 'createdAt'
  > | null
  transactionStatus?: string
}): boolean => {
  if (isFailedFundingAttemptState(params.fundingAttempt)) return false
  if (
    isSettledWalletCashOutStatus(params.transactionStatus) ||
    isSucceededFundingAttemptState(params.fundingAttempt) ||
    isNonActionableWalletTransactionStatus(params.transactionStatus)
  ) {
    return false
  }
  if (
    shouldShowWalletAgentCode({
      fundingAttempt: params.fundingAttempt,
      transactionStatus: params.transactionStatus
    })
  ) {
    return false
  }

  return Boolean(resolveWalletAgentInstructions(params.fundingAttempt)?.code)
}

export const mapCashOutDisplayStatusToLabelKey = (
  cashOutDisplayStatus?: string | null
): string | null => {
  const normalized = String(cashOutDisplayStatus || '').toUpperCase()
  if (normalized === 'READY') return 'billing.wallet.status.ready'
  if (normalized === 'PAID') return 'billing.wallet.status.paid'
  if (normalized === 'FAILED') return 'billing.wallet.status.failed'
  return null
}

export const getWalletCashOutStatusLabelKey = (params: {
  transactionType?: string
  status: 'pending' | 'done' | 'refund' | 'failed' | 'canceled'
  cashOutDisplayStatus?: string | null
}): string => {
  const fromBff = mapCashOutDisplayStatusToLabelKey(params.cashOutDisplayStatus)
  if (fromBff) return fromBff

  const isCashOut = isCashOutWalletTransactionType(params.transactionType)

  if (isCashOut && params.status === 'done') {
    return 'billing.wallet.status.paid'
  }
  if (isCashOut && params.status === 'pending') {
    return 'billing.wallet.status.ready'
  }
  if (isCashOut && params.status === 'failed') {
    return 'billing.wallet.status.failed'
  }

  return `billing.wallet.status.${params.status}`
}
